import { isWordPresent } from "../utils/isWordPresent";
import { WordopolisConfiguration, WordopolisScoreInput, WordopolisWordDefinition } from "../gql/graphql";
import { caseInsensitiveStringEqual } from "../utils/caseInsensitiveStringEqual";

export type WordopolisCellCords = {
  x: number;
  y: number;
};

export type WordopolisWordPlacement = {
  position: WordopolisCellCords[];
  value: string;
};

interface WordopolisWordDefinitionWithDescription extends WordopolisWordDefinition {
  description?: string;
}

export interface WordopolisState {
  configuration: Omit<WordopolisConfiguration, "words"> & {
    words: WordopolisWordDefinitionWithDescription[];
  };
  status: {
    foundWords: string[];
    isGameFinished: boolean;
    startTime: number;
    showWords: boolean;
    nickname?: string;
    finishGameTime?: number;
    isIntroFinished: boolean;
    toRate: string[];
  };
  stage: {
    width: number;
    height: number;
  };
  results: {
    [key in string]: WordopolisScoreInput;
  };
}

export enum WordopolisActionKind {
  INSERT_WORD = "INSERT_WORD",
  END_GAME = "END_GAME",
  TOGGLE_SHOW_WORDS = "TOGGLE_SHOW_WORDS",
  FINISH_INTRO = "FINISH_INTRO",
  RATE_WORD = "RATE_WORD",
  SET_NICKNAME = "SET_NICKNAME",
}

export type WordopolisAction =
  | { type: WordopolisActionKind.INSERT_WORD; payload: { word: string; time: number } }
  | { type: WordopolisActionKind.END_GAME; payload: { time: number } }
  | { type: WordopolisActionKind.TOGGLE_SHOW_WORDS }
  | { type: WordopolisActionKind.FINISH_INTRO }
  | { type: WordopolisActionKind.RATE_WORD; payload: { ratingScore: number } }
  | { type: WordopolisActionKind.SET_NICKNAME; payload: { nickname: string } };

export const wordopolisReducer = (state: WordopolisState, action: WordopolisAction): WordopolisState => {
  switch (action.type) {
    case WordopolisActionKind.TOGGLE_SHOW_WORDS: {
      return {
        ...state,
        status: {
          ...state.status,
          showWords: !state.status.showWords,
        },
      };
    }
    case WordopolisActionKind.FINISH_INTRO: {
      return {
        ...state,
        status: {
          ...state.status,
          isIntroFinished: true,
          startTime: Date.now(),
        },
      };
    }
    case WordopolisActionKind.RATE_WORD: {
      const {
        status: { toRate },
        results,
      } = state;
      const { ratingScore } = action.payload;
      const [word, ...newToRate] = toRate;

      return {
        ...state,
        status: {
          ...state.status,
          toRate: newToRate,
        },
        results: {
          ...results,
          [word]: {
            ...results[word],
            rating: ratingScore,
          },
        },
      };
    }
    case WordopolisActionKind.INSERT_WORD: {
      const {
        configuration: { words: wordsPlacements, showRatingScale, attributes },
        status: { foundWords, toRate },
      } = state;
      const word = action.payload.word.toLowerCase();
      const time = Math.round(action.payload.time);
      const attribute = attributes.find((attribute) => caseInsensitiveStringEqual(attribute.name, word));
      const attributeId = attribute?.id;

      if (!attributeId) {
        throw new Error(`WORDOPOLIS: attribute with name ${word} not found`);
      }

      const newFoundWords = [...foundWords];
      newFoundWords.push(word);
      const newIsGameFinished = newFoundWords.length === wordsPlacements.length;
      const newToRate = [...toRate];

      if (showRatingScale) {
        newToRate.push(word);
      }

      return {
        ...state,
        status: {
          ...state.status,
          foundWords: newFoundWords,
          isGameFinished: newIsGameFinished,
          toRate: newToRate,
        },
        results: {
          ...state.results,
          [word]: {
            attributeId,
            score: attribute.points,
            time,
            rating: 0,
          },
        },
      };
    }
    case WordopolisActionKind.SET_NICKNAME: {
      return {
        ...state,
        status: {
          ...state.status,
          nickname: action.payload.nickname,
        },
      };
    }
    case WordopolisActionKind.END_GAME: {
      const {
        configuration: { showRatingScale, words: wordsPlacements, attributes },
        status: { foundWords, toRate },
      } = state;
      const words = wordsPlacements.map((word) => word.value);

      const notFoundWords = words.filter((word) => !isWordPresent(word, foundWords));

      return {
        ...state,
        status: {
          ...state.status,
          isGameFinished: true,
          finishGameTime: action.payload.time,
          showWords: false,
          toRate: showRatingScale ? [...toRate, ...notFoundWords] : [],
        },
        results: {
          ...state.results,
          ...notFoundWords.reduce((acc, word) => {
            const attributeId = attributes.find((attribute) => caseInsensitiveStringEqual(attribute.name, word))?.id;

            if (!attributeId) {
              throw new Error(`WORDOPOLIS: attribute with name ${word} not found`);
            }

            return {
              ...acc,
              [word]: {
                attributeId,
                score: 0,
                time: 0,
                rating: 0,
              },
            };
          }, {}),
        },
      };
    }
    default:
      return state;
  }
};
